/**
 * ExportCard — Individual Pokémon card for the export image.
 *
 * Design based on /dexbuilder reference (PokemonCard).
 * Layout: [Left: Sprite + Meta] | [Right: Name + Types, Moves + Stats]
 * Uses inline styles for reliable capture with html-to-image.
 */
import React from 'react';
import { formatPokemonName, formatAbilityName, formatItemName, formatMoveName } from '../../data/pokemonTranslations';

const TYPE_COLORS = {
  normal: '#A8A77A', fire: '#EE8130', water: '#6390F0', electric: '#F7D02C',
  grass: '#7AC74C', ice: '#96D9D6', fighting: '#C22E28', poison: '#A33EA1',
  ground: '#E2BF65', flying: '#A98FF3', psychic: '#F95587', bug: '#A6B91A',
  rock: '#B6A136', ghost: '#735797', dragon: '#6F35FC', dark: '#705746',
  steel: '#B7B7CE', fairy: '#D685AD', stellar: '#40B5A5',
};

const SLOT_COLORS = [
  '#6366f1', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4',
];

const STATS = [
  { label: 'HP',  name: 'hp',              evKey: 'hp'  },
  { label: 'Atk', name: 'attack',          evKey: 'atk' },
  { label: 'Def', name: 'defense',         evKey: 'def' },
  { label: 'SpA', name: 'special-attack',  evKey: 'spa' },
  { label: 'SpD', name: 'special-defense', evKey: 'spd' },
  { label: 'Spe', name: 'speed',           evKey: 'spe' },
];

export function ExportCard({ slot, language, index }) {
  const borderColor = SLOT_COLORS[index] || '#6366f1';

  if (!slot || !slot.data) {
    return (
      <div style={{
        background: 'rgba(20, 20, 35, 0.95)',
        borderRadius: 12,
        border: `2px dashed ${borderColor}44`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 200,
        color: 'rgba(255,255,255,0.2)',
        fontSize: 13,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}>
        —
      </div>
    );
  }

  const { data, nickname, level, ability, item, moves, nature, shiny, teraType, evs } = slot;
  const displayName = nickname || formatPokemonName(data.name, language);
  const types = data.types?.map(t => t.type.name) || [];
  const displayAbility = formatAbilityName(ability || data.abilities?.[0]?.ability.name, language);

  const itemNameStr = typeof item === 'object' ? item?.name : item;
  const itemSpriteUrl = typeof item === 'object'
    ? item?.sprite
    : (item ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${item}.png` : '');

  const spriteUrl = shiny
    ? (data.sprites?.front_shiny || data.sprites?.front_default)
    : data.sprites?.front_default;

  const displayMoves = (moves || []).filter(Boolean).slice(0, 4);

  return (
    <div style={{
      position: 'relative',
      borderRadius: 12,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 200,
      background: 'linear-gradient(145deg, rgba(28, 28, 48, 0.98) 0%, rgba(18, 18, 38, 0.98) 100%)',
      border: `2px solid ${borderColor}`,
      color: '#fff',
      fontFamily: "'Inter', system-ui, sans-serif",
      padding: '12px 14px 10px 14px',
      boxSizing: 'border-box',
    }}>
      {/* Top accent strip */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${borderColor}cc, ${borderColor}44)`,
      }} />

      {/* Main 2-column: [Sprite + Meta] | [Name + Types, Moves + Stats] */}
      <div style={{
        display: 'flex',
        gap: 12,
        flex: 1,
        overflow: 'hidden',
      }}>
        {/* LEFT: Sprite + Meta */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 110,
          flexShrink: 0,
        }}>
          {/* Sprite */}
          <div style={{
            width: 88, height: 88,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 6,
          }}>
            {spriteUrl && (
              <img
                src={spriteUrl}
                alt={data.name}
                crossOrigin="anonymous"
                loading="eager"
                decoding="sync"
                style={{
                  width: 80, height: 80,
                  objectFit: 'contain',
                  imageRendering: 'pixelated',
                  display: 'block',
                }}
              />
            )}
          </div>

          {/* Meta: Ability */}
          <div style={{
            fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: 'rgba(255,255,255,0.35)',
            width: '100%', textAlign: 'left', marginBottom: 1,
          }}>
            {language === 'es' ? 'Habilidad' : 'Ability'}
          </div>
          <div style={{
            fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.75)',
            textTransform: 'capitalize', width: '100%', textAlign: 'left',
            marginBottom: 6,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {displayAbility}
          </div>

          {/* Meta: Nature */}
          <div style={{
            fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: 'rgba(255,255,255,0.35)',
            width: '100%', textAlign: 'left', marginBottom: 1,
          }}>
            {language === 'es' ? 'Naturaleza' : 'Nature'}
          </div>
          <div style={{
            fontSize: 10, fontWeight: 600, color: '#A78BFA',
            textTransform: 'capitalize', width: '100%', textAlign: 'left',
            marginBottom: 6,
          }}>
            {nature ? `${nature.charAt(0).toUpperCase()}${nature.slice(1)}` : '—'}
          </div>

          {/* Meta: Held Item */}
          <div style={{
            fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: 'rgba(255,255,255,0.35)',
            width: '100%', textAlign: 'left', marginBottom: 2,
          }}>
            {language === 'es' ? 'Objeto' : 'Held Item'}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 4,
            width: '100%',
          }}>
            {itemSpriteUrl && itemNameStr ? (
              <img
                src={itemSpriteUrl}
                alt={formatItemName(itemNameStr, language)}
                crossOrigin="anonymous"
                loading="eager"
                decoding="sync"
                onError={e => { e.target.style.display = 'none'; }}
                style={{ width: 16, height: 16, flexShrink: 0, display: 'block' }}
              />
            ) : (
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)', flexShrink: 0,
                display: 'inline-block',
              }} />
            )}
            <span style={{
              fontSize: 9, color: 'rgba(255,255,255,0.6)',
              textTransform: 'capitalize',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {itemNameStr ? formatItemName(itemNameStr, language) : '—'}
            </span>
          </div>
        </div>

        {/* RIGHT: Name + Types, Moves + Stats */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}>
          {/* Name + Types */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 8,
            marginTop: 2,
          }}>
            <span style={{
              fontSize: 18, fontWeight: 800,
              textTransform: 'capitalize', color: '#fff',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              letterSpacing: '-0.02em',
            }}>
              {displayName}
              {shiny && ' ✨'}
              {teraType && (
                <span style={{
                  display: 'inline-block', marginLeft: 4,
                  width: 14, height: 14, borderRadius: 3,
                  background: TYPE_COLORS[teraType] || '#888',
                  fontSize: 7, color: '#fff',
                  textAlign: 'center', lineHeight: '14px',
                  fontWeight: 'bold', verticalAlign: 'middle',
                }}>T</span>
              )}
            </span>
            {types.slice(0, 2).map(type => (
              <span key={type} style={{
                fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
                background: TYPE_COLORS[type] || '#666', color: '#fff',
                padding: '2px 6px', borderRadius: 4,
                letterSpacing: '0.05em', whiteSpace: 'nowrap',
              }}>
                {type}
              </span>
            ))}
          </div>

          {/* Moves + Stats side by side */}
          <div style={{
            display: 'flex',
            gap: 10,
            flex: 1,
            overflow: 'hidden',
          }}>
            {/* Moves column */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-around',
              flex: 1,
              minWidth: 0,
            }}>
              {[0, 1, 2, 3].map(i => {
                const move = displayMoves[i];
                return (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '2px 0',
                  }}>
                    <span style={{
                      width: 8, height: 8,
                      borderRadius: '50%',
                      background: move ? `${borderColor}55` : 'rgba(255,255,255,0.06)',
                      border: move ? `1.5px solid ${borderColor}99` : '1.5px solid rgba(255,255,255,0.1)',
                      flexShrink: 0,
                      display: 'inline-block',
                    }} />
                    <span style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: move ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.2)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textTransform: 'capitalize',
                    }}>
                      {move ? formatMoveName(move, language) : '—'}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Separator */}
            <div style={{
              width: 1,
              background: `${borderColor}33`,
              alignSelf: 'stretch',
              margin: '4px 0',
            }} />

            {/* Stats column */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-around',
              flexShrink: 0,
              width: 80,
            }}>
              {STATS.map(({ label, name, evKey }) => {
                const base = data.stats?.find(s => s.stat.name === name)?.base_stat || 0;
                const ev = evs?.[evKey] || 0;
                const hasEV = ev > 0;
                return (
                  <div key={label} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1px 0',
                  }}>
                    <span style={{
                      fontSize: 9, fontWeight: 700,
                      color: hasEV ? '#FBBF24' : 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                    }}>
                      {label}
                    </span>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: '#fff',
                      fontVariantNumeric: 'tabular-nums',
                      fontFamily: "'Inter', monospace",
                    }}>
                      {base}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer: Level */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 4,
        paddingTop: 4,
        borderTop: `1px solid ${borderColor}33`,
        fontSize: 9,
        color: 'rgba(255,255,255,0.45)',
      }}>
        <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>
          Lv.{level ?? 50}
        </span>
      </div>
    </div>
  );
}

export default ExportCard;
